import type { DefaultRootState } from 'react-redux';

import type { Nullable } from 'types';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum TrafficLightStatus {
    Narrow = 'NARROW',
    Medium = 'MEDIUM',
    Wide = 'WIDE',
    Disabled = 'UNDEFINED',
}

export enum PredictWindowMode {
    WithSelector = 'withSelector',
    WithoutSelector = 'withoutSelector',
}

export interface PredictWindowState {
    audienceReach: Nullable<number>;
    profRolesReachCategory: Nullable<TrafficLightStatus>;
    predictedProfRoles: Nullable<string[]>;
    vacancyProfRoleId: Nullable<string>;
    mode: PredictWindowMode;
    isReady: boolean;
}

// ----------------------- actions -------------------------------------
const SET_VACANCY_PROF_ROLE_ID = 'SET_VACANCY_PROF_ROLE_ID';
const SET_PREDICTED_PROF_ROLES = 'SET_PREDICTED_PROF_ROLES';
const SET_AUDIENCE_DATA = 'SET_AUDIENCE_DATA';
const SET_IS_PREDICT_WINDOW_READY = 'SET_IS_PREDICT_WINDOW_READY';
const SET_PREDICT_WINDOW_INITIAL_STATE = 'SET_PREDICT_WINDOW_INITIAL_STATE';

interface PayloadTypes {
    [SET_VACANCY_PROF_ROLE_ID]: NonNullable<PredictWindowState['vacancyProfRoleId']>;
    [SET_PREDICTED_PROF_ROLES]: NonNullable<PredictWindowState['predictedProfRoles']> | [];
    [SET_AUDIENCE_DATA]: NonNullable<Pick<PredictWindowState, 'audienceReach' | 'profRolesReachCategory'>>;
    [SET_IS_PREDICT_WINDOW_READY]: PredictWindowState['isReady'];
    [SET_PREDICT_WINDOW_INITIAL_STATE]: void;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setVacancyProfRole = ActionCreator(SET_VACANCY_PROF_ROLE_ID);
export const setPredictedProfRoles = ActionCreator(SET_PREDICTED_PROF_ROLES);
export const setAudienceData = ActionCreator(SET_AUDIENCE_DATA);
export const setIsPredictWindowReady = ActionCreator(SET_IS_PREDICT_WINDOW_READY);
export const setPredictWindowInitialState = ActionCreator(SET_PREDICT_WINDOW_INITIAL_STATE);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
export const PREDICT_WINDOW_INITIAL_STATE: PredictWindowState = {
    audienceReach: null,
    profRolesReachCategory: null,
    predictedProfRoles: null,
    vacancyProfRoleId: null,
    mode: PredictWindowMode.WithoutSelector,
    isReady: false,
};

export default createReducer<PredictWindowState, PayloadTypes>(PREDICT_WINDOW_INITIAL_STATE, {
    [SET_VACANCY_PROF_ROLE_ID]: (state, { payload }) => ({
        ...state,
        vacancyProfRoleId: payload,
    }),
    [SET_PREDICTED_PROF_ROLES]: (state, { payload }) => ({
        ...state,
        predictedProfRoles: payload,
        mode: payload.length ? PredictWindowMode.WithSelector : PredictWindowMode.WithoutSelector,
    }),
    [SET_AUDIENCE_DATA]: (state, { payload }) => ({
        ...state,
        ...payload,
        isReady: true,
    }),
    [SET_IS_PREDICT_WINDOW_READY]: (state, { payload }) => ({
        ...state,
        isReady: payload,
    }),
    [SET_PREDICT_WINDOW_INITIAL_STATE]: () => ({
        ...PREDICT_WINDOW_INITIAL_STATE,
    }),
});
// ----------------------- /reducer --------------------------------------

// ----------------------- selectors --------------------------------------
export const selectPredictWindowState = (state: DefaultRootState): PredictWindowState => state.predictWindow;

export const selectProfRolesReachCategory = (state: DefaultRootState): PredictWindowState['profRolesReachCategory'] =>
    selectPredictWindowState(state).profRolesReachCategory;
export const selectAudienceReach = (state: DefaultRootState): PredictWindowState['audienceReach'] =>
    selectPredictWindowState(state).audienceReach;
export const selectPredictedProfRoles = (state: DefaultRootState): PredictWindowState['predictedProfRoles'] =>
    selectPredictWindowState(state).predictedProfRoles;
export const selectPredictWindowMode = (state: DefaultRootState): PredictWindowState['mode'] =>
    selectPredictWindowState(state).mode;
export const selectVacancyProfRoleId = (state: DefaultRootState): PredictWindowState['vacancyProfRoleId'] =>
    selectPredictWindowState(state).vacancyProfRoleId;
export const selectIsReadyPredictWindow = (state: DefaultRootState): PredictWindowState['isReady'] =>
    selectPredictWindowState(state).isReady;
// ----------------------- /selectors --------------------------------------
